// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-02:
// Student journey cluster article: the free legal research stack. Answers
// the fragmented-libguide problem in one student-first explainer: what is
// free (Find Case Law, BAILII, legislation.gov.uk, UKSC), what the
// subscription databases add, and how to research without getting burned.
// Access mechanics vary by university, so subscription access is always
// framed as "via your university library". No coverage claim goes beyond
// what the services say about themselves.

import type { ResourceSection, ResourceSourceGroup } from "./types";

export const freeLegalResearchToolsSections: ResourceSection[] = [
  {
    id: "the-paywall-myth",
    heading: "The paywall myth",
    paragraphs: [
      "Many judgments and legislative texts are available through free public services. Coverage varies by court, date and jurisdiction. Use your university library when a judgment, law report or commentary is absent, and check the status of each authority before relying on it. Subscription databases can add material that is not freely available, as well as commentary and research tools.",
      "Knowing the free stack matters twice over. It makes you independent as a student, on the train, at home, after graduation, on a placement where nobody hands you a password. And it teaches you to work from primary sources, which is the habit that separates lawyers from people who read about law.",
    ],
    callout: {
      title: "The stack in one line",
      body: "Find Case Law and BAILII for judgments, legislation.gov.uk for statutes, the Supreme Court's own site for its decisions, and your university library for everything the subscription databases add on top.",
    },
  },
  {
    id: "find-case-law",
    heading: "Find Case Law: the official judgment service",
    paragraphs: [
      "Find Case Law is run by The National Archives. It publishes selected decisions from England and Wales courts and tribunals, plus UK Supreme Court and Privy Council judgments. Date ranges vary by court and it is not a complete record. It contains no decisions from Scottish, Northern Irish or Irish courts or tribunals; UK Supreme Court judgments may still concern appeals originating in Scotland or Northern Ireland. Check the service's coverage page before searching.",
      "Treat it as your first stop for a modern English or Welsh case. The search accepts party names, citations and keywords, and the judgments are free to read and share. What it does not give you is editorial extras: no headnotes, no case history tables, no signals about later treatment. That is the gap the subscription services fill.",
    ],
    links: [
      { label: "Find Case Law (The National Archives)", href: "https://caselaw.nationalarchives.gov.uk/" },
      { label: "Find Case Law: courts and coverage", href: "https://caselaw.nationalarchives.gov.uk/courts-and-coverage" },
      { label: "Scottish Courts and Tribunals Service: judgments", href: "https://www.scotcourts.gov.uk/judgments/" },
      { label: "Judiciary NI: judicial decisions and directions", href: "https://www.judiciaryni.uk/judicial-decisions" },
    ],
  },
  {
    id: "bailii",
    heading: "BAILII: the wide free archive",
    paragraphs: [
      "BAILII, the British and Irish Legal Information Institute, has been the free backbone of UK legal research for decades. Its archive is broader than Find Case Law in both jurisdiction and history: England and Wales alongside Scotland, Northern Ireland, Ireland and more, with many older decisions that predate the neutral-citation era.",
      "When a case is too old or too far from the senior English courts for Find Case Law, BAILII is usually where you will find it. The interface is plain and the search rewards precision, so arrive with a party name or citation rather than a vague topic where you can.",
    ],
    links: [{ label: "BAILII: British and Irish Legal Information Institute", href: "https://www.bailii.org/" }],
  },
  {
    id: "legislation-gov-uk",
    heading: "legislation.gov.uk: statutes as they stand",
    paragraphs: [
      "For legislation the official source is legislation.gov.uk, which publishes UK Acts and statutory instruments and, crucially, marks revision status: whether the text you are reading incorporates later amendments, and where changes are still outstanding.",
      "That revision awareness is the whole game. A depressing number of student errors trace to reading a section as originally enacted when it was amended years ago. Two honest caveats from the service itself: newly made amendments can take time to be incorporated, with outstanding ones flagged in the changes banner on each page, and much secondary legislation is published only as originally made rather than in revised form. So before you rely on a provision, check what the page tells you about its current state, and note the date you checked. Markers notice, and so do supervisors on placements.",
    ],
    links: [
      { label: "legislation.gov.uk: official UK legislation with revision status", href: "https://www.legislation.gov.uk/" },
    ],
  },
  {
    id: "statute-four-passes",
    heading: "Read a statute in four passes",
    paragraphs: [
      "First identify the exact section and legal question. Second read its defined terms and cross-references. Third trace conditions, exceptions and relevant schedules. Fourth check which version applies to your facts: commencement, territorial extent, territorial application, amendments and outstanding revision notices.",
      "Extent describes the legal jurisdictions in which a provision forms part of the law; application describes the situations and places it governs. Neither an Act's title nor its enactment date establishes that a particular section is in force for your problem. Consult commencement orders and annotations, record the date of your check, and ask your law librarian when the position is unclear.",
    ],
    links: [{ label: "legislation.gov.uk: understanding legislation", href: "https://www.legislation.gov.uk/understanding-legislation" }],
  },
  {
    id: "supreme-court",
    heading: "The Supreme Court's own site, and press summaries",
    paragraphs: [
      "The UK Supreme Court publishes its decided cases directly, and alongside each judgment it publishes a press summary: a short official explanation of what the case decided and why. For a long, difficult appellate decision, reading the press summary first is one of the best orientation tricks available to a student, and it comes from the court itself rather than a commercial summariser.",
      "Orientation is the right word, though. The summary is not the judgment, and it is not what you cite. Use it the way our case-reading guide uses headnotes: as a map before the close reading, never as a substitute for it.",
    ],
    links: [{ label: "UK Supreme Court: decided cases and press summaries", href: "https://www.supremecourt.uk/cases" }],
  },
  {
    id: "what-subscriptions-add",
    heading: "What Westlaw and Lexis actually add",
    paragraphs: [
      "Check which subscription databases your university library provides and how to sign in; access varies by institution. These services may add headnotes summarising cases, case histories, journal articles and commentary, and citator tools showing later treatment, such as whether a decision has been followed, distinguished, doubted or overruled. Read the later judgment rather than relying only on a status label.",
      "That last feature is the professional reason the databases matter: checking whether an authority is still good law is a step real legal work cannot skip. Learn one database properly during first year, through your library's training rather than trial and error. The skill is expected on vacation schemes and mini-pupillages, and access mechanics vary between universities, so your law librarian is the right person to ask, not a general search engine.",
    ],
    links: [
      {
        label: "University of York law subject guide: finding cases and legislation",
        href: "https://subjectguides.york.ac.uk/law/cases-and-legislation",
      },
      {
        label: "Bodleian Law Library: the main legal databases explained",
        href: "https://www.bodleian.ox.ac.uk/libraries/law/legal-databases",
      },
    ],
  },
  {
    id: "which-tool-when",
    heading: "Which tool, when",
    paragraphs: ["A simple decision habit covers most student research situations:"],
    bullets: [
      "You have a neutral citation from a reading list: Find Case Law first, BAILII second.",
      "The case is Scottish or Northern Irish: start with the relevant official court service; try BAILII for additional material. For older or Irish cases, try BAILII and your library. Coverage is not complete.",
      "You need a statute or statutory instrument, or to check whether a section has been amended: legislation.gov.uk.",
      "It is a Supreme Court case and you want your bearings before the full judgment: the court's own press summary.",
      "You need commentary, journals, or to check whether an authority is still good law: the subscription databases, through your university library.",
      "You are citing in assessed work: format per OSCOLA and your school's house style, whatever service you read the case on.",
    ],
  },
  {
    id: "research-habits",
    heading: "Habits that keep your research honest",
    paragraphs: [
      "The mechanical part of legal research is learnable in an afternoon. The habits are what carry marks and, later, professional credibility. Keep a running log of what you searched, where, and what you found; it turns a chaotic evening of tabs into something you can retrace. Record the citation and the date you accessed each source at the moment you use it, not during a deadline-night reconstruction.",
      "Above all: never cite what you have not read. Not a case known only through a textbook footnote, not a section known only through a lecture slide, and not anything known only through an AI-generated summary, Durmah's included. Summaries orient; sources decide. If checking the source changes your view of what it says, the check just did its job.",
    ],
    callout: {
      title: "The professional standard, early",
      body: "Reading the authority before relying on it is not a study tip; it is the working standard of the profession you are training for. Students who build the habit in first year never have to unlearn its absence.",
    },
  },
  {
    id: "common-mistakes",
    heading: "Common mistakes, so you can skip them",
    paragraphs: ["Most research trouble in the first two years comes from a short list of shortcuts:"],
    bullets: [
      "Citing a blog, revision site or AI summary instead of the judgment or section itself.",
      "Reading a statute as enacted when it has been amended. Check revision status on legislation.gov.uk.",
      "Trusting the first search-engine result over the official source it paraphrases.",
      "Treating a textbook as the authority rather than the map to the authority.",
      "Not checking whether a case has been overruled or doubted before building an argument on it.",
      "Leaving all referencing details for later, then reconstructing citations from memory.",
    ],
  },
  {
    id: "where-durmah-fits",
    heading: "Where Durmah fits, honestly",
    paragraphs: [
      "Check every legal proposition against primary sources. Durmah is an independent study aid; AI can make mistakes, and current AI availability and content-review limits are explained on our Service transparency page.",
      "Durmah will not run your research for you, and the reading stays yours. What it can do is help you understand what you find: the terminology inside a judgment, the structure of a provision, the reasoning you are about to rely on. Research finds the material; understanding it is where your own ability grows.",
    ],
  },
];

export const freeLegalResearchToolsSources: ResourceSourceGroup[] = [
  {
    topic: "Free official sources",
    sources: [
      { label: "Find Case Law: England and Wales decisions plus UK Supreme Court and Privy Council", href: "https://caselaw.nationalarchives.gov.uk/" },
      { label: "BAILII: British and Irish Legal Information Institute", href: "https://www.bailii.org/" },
      { label: "legislation.gov.uk: official UK legislation", href: "https://www.legislation.gov.uk/" },
      { label: "UK Supreme Court: decided cases", href: "https://www.supremecourt.uk/cases" },
    ],
  },
  {
    topic: "University library guidance",
    sources: [
      {
        label: "University of York: cases and legislation subject guide",
        href: "https://subjectguides.york.ac.uk/law/cases-and-legislation",
      },
      {
        label: "Bodleian Law Library: legal databases",
        href: "https://www.bodleian.ox.ac.uk/libraries/law/legal-databases",
      },
    ],
  },
];
